import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, useEffect } from 'react';
import { ApiError, closeAdminSession } from './api';

/**
 * Who is connected, held once by the shell and read by the screens (tableau de
 * bord spec §5, A3; auth Tahiti §6).
 *
 * It used to hold the TOKEN. It cannot any more, and that is the point of the
 * lot: the credential is an `httpOnly` cookie, so no JavaScript on this page
 * can read it. What the screens actually needed from it was never the secret -
 * it was an identity to key their caches on and a way to say « we were
 * refused ». Both survive; the secret is gone.
 */

export interface AdminSession {
  /**
   * The operator's address, or `null` when the caller came in by the SHARED
   * TOKEN - the e2e suites, the capture scripts, the two CI jobs (T6/T8).
   *
   * `null` is a real state and not an absence: the header names a deployment
   * and never a person, so there is no address to invent. It is deliberately
   * NOT a way to ask which credential the guard accepted - the guard never
   * says - it is the account door reporting whether it has an account.
   */
  email: string | null;
  /**
   * A screen was refused: reopen the door. Stable across renders - it is a
   * dependency of the effect below, and a new identity every render would spin.
   */
  onRejected: () => void;
}

/**
 * The one key the door, the shell and the sign-out all read.
 *
 * Exported since issue #24, because the sign-out moved out of the shell and
 * into the rail: two modules now invalidate this query, and two spellings of a
 * cache key are two caches that eventually disagree about who is connected.
 */
export const ADMIN_SESSION_KEY = ['admin-session'] as const;

const AdminSessionContext = createContext<AdminSession | null>(null);

export const AdminSessionProvider = AdminSessionContext.Provider;

export function useAdminSession(): AdminSession {
  const session = useContext(AdminSessionContext);
  if (session === null) {
    // A screen rendered outside the shell would otherwise fetch with no
    // session at all and read the 401 as a refusal rather than as a wiring
    // mistake.
    throw new Error('useAdminSession must be used inside the /admin shell');
  }
  return session;
}

/**
 * The way out, in ONE place (issue #24).
 *
 * It used to live in the shell's header, as a mutation beside the identity
 * line. The rail took the button, and S2 put a second one inside Réglages -
 * because below 768 px the rail is a bottom bar with room for six destinations
 * and no seventh slot for an action. Two buttons, one gesture: written twice,
 * the two would eventually disagree about whether a failed round trip still
 * closes the door. `words-interface` learned this first and says so at the head
 * of its own `use-logout.ts`.
 *
 * `onSettled` and not `onSuccess`: if the request failed the cookie is still
 * there, and the honest thing is to ask the backend again rather than to show a
 * door that would let them straight back in.
 */
export function useAdminSignOut(): { signOut: () => void; signingOut: boolean } {
  const queryClient = useQueryClient();
  const logout = useMutation({
    mutationFn: closeAdminSession,
    onSettled: () => queryClient.invalidateQueries({ queryKey: ADMIN_SESSION_KEY }),
  });
  return { signOut: () => logout.mutate(), signingOut: logout.isPending };
}

/**
 * Sends a screen's 401 back up to the shell, which reopens the door (A8).
 *
 * WHY THIS IS AN EFFECT, in a repo that proscribes them: the ban is on FETCHING
 * (`useEffect`+`fetch` instead of TanStack Query). This hook fetches nothing -
 * it reacts to an error the screen's own query already received.
 *
 * WHY IT SURVIVES THIS LOT. A8 kept it because the shell had no query of its
 * own and « there is no cheap validation endpoint to give it one ». There is
 * one now (`GET /api/admin/session`), and the shell asks it - so it no longer
 * waits for a screen to discover that nobody is connected. That closes the
 * FIRST render and nothing after it: a cookie expires on its own clock, in the
 * middle of an afternoon, between two requests of a screen that is already
 * open. The screen is then the first to see it, exactly as before.
 *
 * Calling `onRejected()` during render is the alternative, and React refuses
 * it: it is a state update on another component.
 *
 * Returns the verdict so the caller can keep suppressing its own error message -
 * without it, the screen would flash « n'a pas pu être chargé » for one frame
 * before the shell swaps in the door.
 */
export function useRejectOn401(error: unknown): boolean {
  const { onRejected } = useAdminSession();
  const rejected = error instanceof ApiError && error.status === 401;

  useEffect(() => {
    if (rejected) onRejected();
  }, [rejected, onRejected]);

  return rejected;
}
